import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl, prettySize } from "@/v2/lib/files";
import { GlassCard, Chip, Skeleton, Modal, Confirm, PageHead, UploadBtn, Toggle } from "@/v2/admin/ui";
import {
  Paperclip, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Download, Link2, FileText, Pencil,
} from "lucide-react";

function LinkModal({ cid, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: "", url: "", downloadable: true, hidden: false });
  const save = async () => {
    if (!f.title.trim() || !f.url.trim()) return toast.error("العنوان والرابط مطلوبان");
    try {
      await api.post(`/admin/challenges/${cid}/files`, { ...f, kind: "link" });
      toast.success("تمت إضافة الرابط");
      qc.invalidateQueries({ queryKey: ["adm-challenges"] });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحفظ"); }
  };
  return (
    <Modal title="إضافة رابط" onClose={onClose} testId="file-link-modal">
      <div className="space-y-3">
        <div><label className="v2-label">العنوان</label><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="link-title" className="v2-input" /></div>
        <div><label className="v2-label">الرابط</label><input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} dir="ltr" data-testid="link-url" className="v2-input" placeholder="https://..." /></div>
        <Toggle checked={f.downloadable} onChange={(v) => setF({ ...f, downloadable: v })} label="قابل للتحميل (وإلا للعرض فقط)" testId="link-downloadable" />
        <Toggle checked={f.hidden} onChange={(v) => setF({ ...f, hidden: v })} label="مخفي عن الموظفين" testId="link-hidden" />
      </div>
      <button onClick={save} data-testid="link-save" className="v2-btn w-full mt-5">حفظ</button>
    </Modal>
  );
}

function EditModal({ cid, file, onClose }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(file.title || "");
  const save = async () => {
    try {
      await api.patch(`/admin/challenges/${cid}/files/${file.id}`, { title });
      toast.success("تم التعديل");
      qc.invalidateQueries({ queryKey: ["adm-challenges"] });
      onClose();
    } catch { toast.error("تعذّر التعديل"); }
  };
  return (
    <Modal title="تعديل اسم الملف" onClose={onClose} testId="file-edit-modal">
      <label className="v2-label">العنوان</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="file-edit-title" className="v2-input" />
      <button onClick={save} data-testid="file-edit-save" className="v2-btn w-full mt-5">حفظ</button>
    </Modal>
  );
}

export default function Files() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });
  const [cid, setCid] = useState("");
  const [linkModal, setLinkModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);
  const ch = rows.find((c) => c.id === (cid || rows[0]?.id));
  const files = [...(ch?.files || [])];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["adm-challenges"] });

  const addUploaded = async (res) => {
    try {
      await api.post(`/admin/challenges/${ch.id}/files`, {
        kind: "file", title: res.filename, url: res.url, storage_path: res.storage_path,
        content_type: res.content_type, size: res.size, downloadable: true, hidden: false,
      });
      toast.success("تمت إضافة الملف");
      invalidate();
    } catch { toast.error("تعذّر إضافة الملف"); }
  };

  const patch = async (fid, body, msg) => {
    try { await api.patch(`/admin/challenges/${ch.id}/files/${fid}`, body); toast.success(msg); invalidate(); }
    catch { toast.error("تعذّر التعديل"); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/challenges/${ch.id}/files/${delId}`); toast.success("تم حذف الملف"); invalidate(); }
    catch { toast.error("تعذّر الحذف"); }
    setDelId(null);
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= files.length) return;
    const ids = files.map((f) => f.id);
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    try { await api.post(`/admin/challenges/${ch.id}/files/reorder`, { ids }); invalidate(); }
    catch { toast.error("تعذّر إعادة الترتيب"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-files">
      <PageHead icon={Paperclip} title="إدارة الملفات والمرفقات" sub="رفع ملفات PDF وصور وملفات تدريب، أو إضافة روابط — مع التحكم بالإظهار والتحميل." />

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="v2-label">التحدي</label>
              <select value={cid || rows[0]?.id || ""} onChange={(e) => setCid(e.target.value)} data-testid="files-ch-select" className="v2-input font-bold text-sm">
                {rows.map((c) => <option key={c.id} value={c.id}>{`اليوم ${c.day} — ${c.title}`}</option>)}
              </select>
            </div>
            {ch && <UploadBtn accept=".pdf,.xlsx,.xls,.docx,.pptx,.csv,.zip,image/*" label="رفع ملف" testId="files-upload" className="v2-btn v2-btn-sm" onDone={addUploaded} />}
            {ch && <button onClick={() => setLinkModal(true)} data-testid="files-add-link" className="v2-btn-ghost v2-btn-sm"><Plus size={14} /> إضافة رابط</button>}
          </div>

          {files.length === 0 ? (
            <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد ملفات لهذا التحدي — ارفع أول ملف أو أضف رابطًا.</GlassCard>
          ) : (
            <div className="space-y-3">
              {files.map((f, i) => (
                <GlassCard key={f.id} className="p-4" data-testid={`file-item-${i}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowUp size={12} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === files.length - 1} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowDown size={12} /></button>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center shrink-0">
                      {f.kind === "link" ? <Link2 size={18} /> : <FileText size={18} />}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-bold text-sm">{f.title}</div>
                      <div className="text-[11px] text-[#bcc8cf] truncate" dir="ltr">{f.url}</div>
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        <Chip tone={f.hidden ? "red" : "emerald"}>{f.hidden ? "مخفي" : "ظاهر"}</Chip>
                        <Chip tone={f.downloadable ? "gold" : "muted"}>{f.downloadable ? "قابل للتحميل" : "للعرض فقط"}</Chip>
                        {f.size ? <Chip tone="muted">{prettySize(f.size)}</Chip> : null}
                      </div>
                    </div>
                    <div className="flex gap-1.5 items-center">
                      <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" data-testid={`file-open-${i}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center"><Download size={14} /></a>
                      <button onClick={() => setEditing(f)} data-testid={`file-edit-${i}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center"><Pencil size={14} /></button>
                      <button onClick={() => patch(f.id, { hidden: !f.hidden }, f.hidden ? "تم إظهار الملف" : "تم إخفاء الملف")} data-testid={`file-hide-${i}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center">{f.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button onClick={() => patch(f.id, { downloadable: !f.downloadable }, "تم تحديث صلاحية التحميل")} data-testid={`file-dl-${i}`} className="v2-btn-ghost v2-btn-sm">{f.downloadable ? "للعرض فقط" : "اسمح بالتحميل"}</button>
                      <button onClick={() => setDelId(f.id)} data-testid={`file-del-${i}`} className="w-8 h-8 rounded-lg bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </>
      )}

      {linkModal && ch && <LinkModal cid={ch.id} onClose={() => setLinkModal(false)} />}
      {editing && ch && <EditModal cid={ch.id} file={editing} onClose={() => setEditing(null)} />}
      {delId && <Confirm title="حذف الملف" danger confirmLabel="نعم، احذف"
        body="سيُحذف الملف من هذا التحدي ولن يظهر للموظفين. ستُسجَّل العملية في سجل الإدارة."
        onConfirm={remove} onClose={() => setDelId(null)} testId="file-del-confirm" />}
    </div>
  );
}
